// react
import React, { useState, useEffect } from "react";

// react-router-dom
import { useLocation } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// react-pdf
import { Document, Page, pdfjs } from "react-pdf";

// @mui
import {
  useMediaQuery,
  Box,
  Grid,
  MenuItem,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  Select,
  Switch,
  Tooltip,
} from "@mui/material";
import {
  CloseOutlined as CloseOutlinedIcon,
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";

// components
import { LinearLoadingComponent } from "components/loading/LoadingComponent";
import { Puller } from "components/common/Puller";
import { NewCertConfirm, NewRequestConfirm, InvalidFile } from "components/app/AppFeedback";

// lib/safetwin
import { decryptSeedByPasswordHash4Ed25519 } from "lib/safetwin/crypto/cryptoseed";

// artifacts
import genDocRecord from "artifacts/doc/core/genDocRecord";

// features/databox
import sendCertificationRequest from "features/databox/sendCertificationRequest";
import completeCertificationRequest from "features/databox/completeCertificationRequest";

// services/storage
import uploadFile from "services/storage/uploadFile";
import getFile from "services/storage/getFile";

// firebase
import { getFunctions, httpsCallable } from "firebase/functions";

// utils
import fileToUint8Array from "utils/fileToUint8Array";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const MAX_FILE_SIZE = 25 * 1024 * 1024;

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const DocGenerator = ({ tags, tag, types, setTypes, open, setOpen, handleOpenCertificationSuccessful, handleOpenCertificationError, request, setRequest }) => {
  const { user } = UserAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [keypair, setKeyPair] = useState(null);
  const [source, setSource] = useState("");
  const [blobURL, setBlobURL] = useState("");
  const [storagePath, setStoragePath] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("");
  const [comment, setComment] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tagSelectionOpen, setTagSelectionOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [approval, setApproval] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalidFile, setIsInvalidFile] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) handleReset();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  useEffect(() => {
    if (open) {
      const keypair = decryptSeedByPasswordHash4Ed25519(user.reloadUserInfo.passwordHash);
      setKeyPair(keypair);
    }
  }, [open]);

  useEffect(() => {
    if (request) {
      const path = request.data.storagePath;
      setStoragePath(path);
      setFileName(request.data.fileName);
      setFileType(request.data.fileType);
      setComment(request.data.comment);
      setIsPublic(request.public);

      const fetchFile = async () => {
        const bytearray = await getFile(path);
        setSource(bytearray);
      };

      fetchFile();
    } else {
      setSource("");
      setBlobURL("");
      setStoragePath("");
      setFileName("");
      setFileType("");
      setComment("");
      setIsPublic(false);
    }
  }, [request]);

  const handleGenerate = async () => {
    setConfirm(false);
    setIsLoading(true);

    if (!keypair) {
      console.log("No keypair provided.");
      setIsLoading(false);
      return;
    }

    try {
      let path = storagePath;

      if (!request) {
        const res = await uploadFile(source, fileName, fileType, tag, isPublic, user);
        path = res.storagePath;
        setStoragePath(path);
      }

      const dataBody = { source, storagePath: path, fileName, fileType, comment };
      let result;

      if (tag) result = await genDocRecord(isPublic, user.uid, keypair, dataBody, tag);
      else if (selectedTag) result = await genDocRecord(isPublic, user.uid, keypair, dataBody, selectedTag);
      else result = await genDocRecord(isPublic, user.uid, keypair, dataBody);

      if (result.success) {
        if (request) await completeCertificationRequest(tag, request.id);
        handleOpenCertificationSuccessful();
        if (types && !types.includes("doc")) setTypes([...types, "doc"]);
      } else {
        handleOpenCertificationError();
      }
    } catch (error) {
      console.error("Error in handleGenerate:", error);
      handleOpenCertificationError();
    } finally {
      setIsLoading(false);
      handleReset();
    }
  };

  const handleSendApprovalRequest = async () => {
    setApproval(false);
    setIsLoading(true);

    try {
      const res = await uploadFile(source, fileName, fileType, tag, isPublic, user);
      const requestTag = tag || selectedTag;
      const requestData = { storagePath: res.storagePath, fileName, fileType, comment };
      await sendCertificationRequest("doc", user.uid, isPublic, requestData, requestTag);
    } catch (error) {
      console.error("Error in handleSendApprovalRequest:", error);
      handleOpenCertificationError();
    } finally {
      setIsLoading(false);
      handleReset();
    }
  };

  const handleFileChange = async (target) => {
    if (target?.files?.length) {
      const file = target.files[0];
      if (file.size > MAX_FILE_SIZE) {
        setErrorMessage("File size exceeds 25MB limit.");
        setIsInvalidFile(true);
        resetFileInput();
        return;
      }

      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "pdf" && file.type === "application/pdf") {
        const arr = await fileToUint8Array(file);
        const url = URL.createObjectURL(file);
        setSource(arr);
        setBlobURL(url);
        setFileName(file.name);
        setFileType(file.type);
      } else {
        setErrorMessage(`Invalid file format: ${ext}. Allowed: pdf.`);
        setIsInvalidFile(true);
        resetFileInput();
      }
    }
  };

  const resetFileInput = () => {
    const inp = document.getElementById("certify-pdf-button");
    if (inp) inp.value = null;
  };

  const handleReset = () => {
    setSource("");
    if (blobURL) URL.revokeObjectURL(blobURL);
    setBlobURL("");
    setStoragePath("");
    setFileName("");
    setFileType("");
    setComment("");
    setSelectedTag("");
    setIsPublic(false);
    setIsInvalidFile(false);
    if (request) setRequest(null);
    setOpen(false);
    resetFileInput();
  };

  const handleChange = (e) => setSelectedTag(e.target.value);

  const handleOpen = () => {
    setTagSelectionOpen(true);
    window.history.pushState(null, "");
  };

  const handleClose = () => setTagSelectionOpen(false);

  const handleCloseDialog = () => {
    setSelectedFile(null);
    setCurrentPage(1);
  };

  const handleDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleFileClick = async (path) => {
    try {
      let url = path;

      if (typeof path === "string" && !path.startsWith("http://") && !path.startsWith("https://") && !path.startsWith("blob:")) {
        const functions = getFunctions();
        const getSignedUrl = httpsCallable(functions, "getSignedDownloadUrl");

        const tagId = tag || selectedTag || request?.tagId;
        if (!tagId) {
          console.warn("Missing tagId for secure document preview");
          return;
        }

        const response = await getSignedUrl({ storagePath: path, tagId });
        url = response.data.url;
      }

      const resp = await fetch(url);
      const blob = await resp.blob();

      if (blob.type === "application/pdf") {
        setSelectedFile(url);
      } else {
        console.warn(`Unsupported file type for preview: ${blob.type}`);
      }
    } catch (err) {
      console.error("Error previewing doc:", err);
    }
  };

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Typography variant="h6" fontWeight="bold">
        {t("certify_document")}
      </Typography>
      <Tooltip title={t("close")}>
        <IconButton onClick={handleReset} color="error" edge="end">
          <CloseOutlinedIcon />
        </IconButton>
      </Tooltip>
    </Grid>
  );

  const DocReportForm = (
    <Grid container spacing={2}>
      {request ? (
        <Grid item container alignItems="center">
          <Grid item xs={12}>
            <Button onClick={() => handleFileClick(storagePath)} sx={getButtonStyle("open")}>
              {fileName || t("selected_file")}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <input id="certify-pdf-button" type="file" accept="application/pdf" style={{ display: "none" }} disabled={request} onChange={(e) => handleFileChange(e.target)} />
            <label htmlFor="certify-pdf-button">
              <Button disabled={request} variant="outlined" component="span" startIcon={<FileUploadIcon />} sx={getButtonStyle("open")}>
                {t("select_file")}
              </Button>
            </label>
          </Grid>
          {source ? (
            <Grid item xs={12}>
              <Button onClick={() => handleFileClick(blobURL)} variant="outlined" sx={getButtonStyle("open")}>
                {fileName || t("selected_file")}
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography>{t("no_file_selected")}</Typography>
            </Grid>
          )}
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField margin="dense" id="comments" name="comments" label={t("description")} variant="outlined" fullWidth value={comment} disabled={request} onChange={(e) => setComment(e.target.value)} />
      </Grid>

      {location.pathname === "/my-certifications" && tags && (
        <Grid item xs={12}>
          <Typography gutterBottom>{t("databox")}</Typography>
          <FormControl fullWidth>
            <Select
              id="selected-tag-doc"
              value={selectedTag}
              displayEmpty
              onChange={handleChange}
              open={tagSelectionOpen}
              onOpen={handleOpen}
              onClose={handleClose}
              sx={{ width: isMobile ? "100%" : "50%" }}
            >
              <MenuItem value="">
                <Typography color="gray">{t("no_databox")}</Typography>
              </MenuItem>
              {tags
                .sort((a, b) => a.nickname.localeCompare(b.nickname))
                .map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.nickname}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12}>
        <FormGroup>
          <FormControlLabel
            control={<Switch checked={isPublic} disabled={request} onChange={(event) => setIsPublic(event.target.checked)} inputProps={{ "aria-label": "controlled" }} />}
            label={t("mark_as_public")}
          />
        </FormGroup>
      </Grid>
    </Grid>
  );

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleReset}
          onOpen={() => setOpen(true)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              height: "80%",
              borderTopLeftRadius: "4%",
              borderTopRightRadius: "4%",
            },
          }}
        >
          <Puller />
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" textAlign="center" mt={2} mb={3}>
              {t("certify_document")}
            </Typography>
            {isLoading ? (
              <LinearLoadingComponent />
            ) : (
              <>
                {DocReportForm}
                <Grid container spacing={1} mt={5}>
                  <Grid item xs={12}>
                    <Button fullWidth variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                      {t("cancel")}
                    </Button>
                  </Grid>
                  {!request && (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={getButtonStyle("open")}
                        disabled={isLoading || (location.pathname === "/my-certifications" && (!source || !selectedTag)) || (location.pathname !== "/my-certifications" && !source)}
                        onClick={() => setApproval(true)}
                      >
                        {t("send_request")}
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Button fullWidth variant="outlined" sx={getButtonStyle("open")} disabled={isLoading || !source} onClick={() => setConfirm(true)}>
                      {t("certify")}
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={open}
          onClose={handleReset}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 2,
              p: 2,
              boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <DialogTitle>
            <DialogTitleContent />
          </DialogTitle>
          {isLoading ? (
            <LinearLoadingComponent />
          ) : (
            <>
              <DialogContent>{DocReportForm}</DialogContent>
              <DialogActions>
                <Button variant="outlined" sx={getButtonStyle("close")} onClick={handleReset}>
                  {t("cancel")}
                </Button>
                {!request && (
                  <Button
                    variant="outlined"
                    sx={getButtonStyle("open")}
                    disabled={isLoading || (location.pathname === "/my-certifications" && (!source || !selectedTag)) || (location.pathname !== "/my-certifications" && !source)}
                    onClick={() => setApproval(true)}
                  >
                    {t("send_request")}
                  </Button>
                )}
                <Button variant="outlined" sx={getButtonStyle("open")} disabled={isLoading || !source} onClick={() => setConfirm(true)}>
                  {t("certify")}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      )}

      <NewCertConfirm confirm={confirm} setConfirm={setConfirm} handleGenerate={handleGenerate} />

      <NewRequestConfirm approval={approval} setApproval={setApproval} handleSendApprovalRequest={handleSendApprovalRequest} />

      <InvalidFile open={isInvalidFile} setOpen={setIsInvalidFile} message={errorMessage} />

      {selectedFile && (
        <Dialog
          open={Boolean(selectedFile)}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "15px",
              padding: 1,
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip placement="top" title={t("close")}>
              <IconButton onClick={handleCloseDialog} color="error" aria-label="close">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Document file={selectedFile} onLoadSuccess={handleDocumentLoadSuccess}>
            <Page pageNumber={currentPage} width={isMobile ? 300 : 600} renderAnnotationLayer={false} renderTextLayer={false} />
          </Document>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <IconButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} aria-label="previous page">
              <KeyboardArrowLeftOutlinedIcon />
            </IconButton>
            <Typography>{`${currentPage} / ${numPages}`}</Typography>
            <IconButton onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === numPages} aria-label="next page">
              <KeyboardArrowRightOutlinedIcon />
            </IconButton>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default DocGenerator;
